import {
  mdiChevronRight,
  mdiCloudDownloadOutline,
  mdiEyeOutline,
} from '@mdi/js';
import Icon from '@mdi/react';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardBody,
  CardHeader,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from 'reactstrap';
import fileIcon from '../../../assets/images/fileIcon.png';
import { fetchDocuments } from '../../../store/apis/documentService';
import * as icons from '../../../assets';
import useDownloadBlobByPath from './useDownloadBlobByPath';
import { FileUtil } from '../../../utils';
import { DisplayModal } from '../../Common';

function DocumentsWidget() {
  const [modal, setModal] = useState(false);
  const toggle = () => setModal(!modal);
  const [displayModalOpen, setDisplayModalOpen] = useState(false);
  const [displayModalFile, setDisplayModalFile] = useState(null);
  const [displayIndex, setDisplayIndex] = useState(null);
  const [downloadIndex, setDownloadIndex] = useState(null);

  const { downloadBlobByPathMutation } = useDownloadBlobByPath();

  const { data: documentsData } = useQuery({
    queryKey: ['documents'],
    queryFn: () =>
      fetchDocuments({
        type: 'DASHBOARD',
      }),
    enabled: true,
  });

  const handleDownload = async (docData, index) => {
    if (!docData?.Path || !docData?.Name) {
      toast.error('Document data is incomplete:', docData, {
        className: 'toast-error',
      });
      return;
    }
    try {
      setDownloadIndex(index);
      const blob = await downloadBlobByPathMutation.mutateAsync({
        filePath: docData?.Path,
        fileName: docData?.Name,
      });
      FileUtil.downloadBlobFile(blob, docData?.Name || 'document.pdf', {
        className: 'toast-error',
      });
    } catch (error) {
      toast.error('Failed to download document', {
        className: 'toast-error',
      });
    } finally {
      setDownloadIndex(null);
    }
  };

  const handleDisplayModal = async (docData, index) => {
    if (!docData?.Path || !docData?.Name) {
      toast.error('Document data is incomplete:', docData, {
        className: 'toast-error',
      });
      return;
    }
    try {
      setDisplayIndex(index);
      const blob = await downloadBlobByPathMutation.mutateAsync({
        filePath: docData?.Path,
        fileName: docData?.Name,
      });
      if (!blob) {
        toast.error('Failed to fetch the document for display.', {
          className: 'toast-error',
        });
        return;
      }
      const file = new File([blob], docData?.Name || 'document.pdf', {
        type: blob?.type,
      });
      setDisplayModalFile(file);
      setDisplayModalOpen(true);
    } catch (error) {
      toast.error('Failed to display document', {
        className: 'toast-error',
      });
    } finally {
      setDisplayIndex(null);
    }
  };

  return (
    <>
      <Card
        className="documents-widget"
        style={{ border: '1px solid #98A2B3', borderRadius: '18px' }}
      >
        <CardHeader
          className="pb-2"
          style={{
            borderRadius: '8px 8px 0px 0px',
            borderBottom: 'none',
          }}
        >
          <div className="d-flex flex-row align-items-center justify-content-between gap-2 pb-0">
            <div className="d-flex flex-row align-items-center gap-2">
              <div>
                <img
                  src={icons.iconsDashboardDocuments}
                  alt="Documents Icons"
                  height="20px"
                />
              </div>
              <div>
                <span
                  className="fs-5"
                  style={{ fontWeight: '600', color: '#363F72' }}
                >
                  Documents
                </span>
              </div>
            </div>

            <button
              type="button"
              className="btn btn-link non-btn"
              onClick={toggle}
            >
              <Icon path={mdiChevronRight} size={1} color="#017EBD" />
            </button>
          </div>
          <div
            style={{
              borderTop: '1px solid #d0d5dd',
              width: '100%',
              marginTop: '10px',
            }}
          />
        </CardHeader>
        <CardBody
          className="pt-0"
          style={{ height: 'auto', overflowY: 'auto' }}
        >
          <div className="d-flex flex-column gap-2">
            {documentsData?.map((doc, index) => (
              <div
                className="d-flex flex-row align-items-center justify-content-between pb-2"
                style={{
                  borderBottom: '1px solid #EAECF0',
                }}
                key={index}
              >
                <div className="d-flex flex-row align-items-center gap-2">
                  <img src={fileIcon} alt="file icon" width={17} height={17} />
                  <span style={{ fontWeight: '500' }}>
                    {doc?.Name || 'document.pdf'}
                  </span>
                </div>
                <div className="d-flex flex-row justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-link non-btn"
                    onClick={() => handleDisplayModal(doc, index)}
                    title="View Document"
                  >
                    {downloadBlobByPathMutation.isPending &&
                    index === displayIndex ? (
                      <Spinner size="sm" color="primary" />
                    ) : (
                      <Icon path={mdiEyeOutline} size={0.7} color="#017EBD" />
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-link non-btn"
                    onClick={() => handleDownload(doc, index)}
                    title="Download Document"
                  >
                    {downloadBlobByPathMutation.isPending &&
                    index === downloadIndex ? (
                      <Spinner size="sm" color="primary" />
                    ) : (
                      <Icon
                        path={mdiCloudDownloadOutline}
                        size={0.7}
                        color="#017EBD"
                      />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
      <Modal isOpen={modal} toggle={toggle} size="md" centered>
        <ModalHeader toggle={toggle}>Documents</ModalHeader>
        <ModalBody>
          <div>
            {documentsData &&
              documentsData.length > 0 &&
              documentsData.map((doc, index) => (
                <div
                  className="d-flex flex-row align-items-center justify-content-between"
                  style={{
                    borderBottom: '1px solid #EAECF0',
                    padding: '10px 0',
                  }}
                  key={index}
                >
                  <div className="d-flex flex-row align-items-center gap-2">
                    <img
                      src={fileIcon}
                      alt="file icon"
                      width={20}
                      height={20}
                    />
                    <span style={{ fontWeight: '500' }}>{doc?.Name}</span>
                  </div>
                  <div className="d-flex flex-row justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-link non-btn"
                      onClick={() => handleDisplayModal(doc, index)}
                    >
                      {downloadBlobByPathMutation.isPending &&
                      index === displayIndex ? (
                        <Spinner size="sm" color="primary" />
                      ) : (
                        <Icon path={mdiEyeOutline} size={0.7} color="#017EBD" />
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-link non-btn"
                      onClick={() => handleDownload(doc, index)}
                    >
                      {downloadBlobByPathMutation.isPending &&
                      index === downloadIndex ? (
                        <Spinner size="sm" color="primary" />
                      ) : (
                        <Icon
                          path={mdiCloudDownloadOutline}
                          size={0.7}
                          color="#017EBD"
                        />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            {documentsData && documentsData.length === 0 && (
              <div
                className="d-flex flex-row align-items-center justify-content-center"
                style={{ height: '100px' }}
              >
                <span
                  style={{
                    fontWeight: '500',
                    color: '#98A2B3',
                    fontSize: '16px',
                  }}
                >
                  No documents available
                </span>
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <button type="button" className="custom-btn" onClick={toggle}>
            Close
          </button>
        </ModalFooter>
      </Modal>
      {displayModalOpen && (
        <DisplayModal
          isModalOpen={displayModalOpen}
          toggle={() => setDisplayModalOpen(false)}
          title="View Attachments"
          file={displayModalFile}
          fullscreen
        />
      )}
    </>
  );
}

export default DocumentsWidget;
